import React from "react";
import { motion } from "framer-motion";
import { useI18n } from "@/context/I18nContext";

export default function StorySection({ content }) {
    const { t, lang } = useI18n();
    const title = lang === "es" ? content?.about_title_es : content?.about_title_en;
    const body = lang === "es" ? content?.about_body_es : content?.about_body_en;

    return (
        <section id="story" data-testid="story-section" className="relative py-24 md:py-32 bg-habana-surface overflow-hidden">
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute -left-24 top-1/3 w-[420px] h-[420px] rounded-full bg-habana-red/15 blur-3xl" />
            </div>
            <div className="relative mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9 }}
                    className="lg:col-span-7"
                >
                    <div className="ornament">{t("story_since")}</div>
                    <h2 className="mt-4 font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.05] text-habana-cream">
                        {title?.split(" ").slice(0, -1).join(" ")}{" "}
                        <span className="italic gold-shimmer">{title?.split(" ").slice(-1).join(" ")}</span>
                    </h2>
                    <p className="mt-6 text-lg text-habana-cream/75 leading-[1.85] max-w-xl">
                        {body}
                    </p>
                    <div className="mt-8 grid grid-cols-3 gap-6 max-w-lg">
                        <StatCard number="15+" label={lang === "es" ? "Años de tradición" : "Years of tradition"} />
                        <StatCard number="40+" label={lang === "es" ? "Recetas cubanas" : "Cuban recipes"} />
                        <StatCard number="100%" label={lang === "es" ? "Sabor auténtico" : "Authentic flavor"} />
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.1 }}
                    className="lg:col-span-5 relative"
                >
                    <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-gold/20">
                        <img
                            src="https://images.unsplash.com/photo-1495745512803-dd531415ff55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHx2aW50YWdlJTIwaGF2YW5hJTIwc3RyZWV0JTIwZGFya3xlbnwwfHx8fDE3ODM5Njc0ODB8MA&ixlib=rb-4.1.0&q=85"
                            alt="Vintage Havana"
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                        <div className="absolute bottom-6 left-6 font-script text-gold text-3xl">
                            {t("story_havana")}
                        </div>
                    </div>
                    <div className="absolute -bottom-6 -left-6 hidden md:block rotate-[-3deg] bg-habana-red text-habana-cream px-6 py-4 shadow-2xl">
                        <div className="font-serif italic text-xl">“{t("story_quote")}”</div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}

function StatCard({ number, label }) {
    return (
        <div className="border-l-2 border-gold/40 pl-3">
            <div className="font-serif text-3xl text-gold">{number}</div>
            <div className="text-xs uppercase tracking-widest text-habana-cream/55 mt-1 leading-tight">
                {label}
            </div>
        </div>
    );
}
